import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../../utils/apiConfig';

/**
 * GroupWorkload - Widget "Charge par groupe" pour Planificateur
 */
const GroupWorkload = ({ token, onNavigate }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchWorkload = async () => {
      if (!token) return;

      setLoading(true);
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/dashboard/group-workload`,
          {
            headers: { 'Authorization': `Bearer ${token}` }
          }
        );

        if (!response.ok) throw new Error('Erreur de chargement');

        const result = await response.json();
        setData(result.success ? result.data : null);
      } catch (err) {
        console.error('Erreur fetch workload:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchWorkload();
  }, [token]);

  const getBarColor = (percent) => {
    if (percent >= 80) return '#dc3545';
    if (percent >= 60) return '#ffc107';
    return '#28a745';
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <h4 style={styles.title}>Charge par groupe</h4>
        <div style={styles.loading}>Chargement...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <h4 style={styles.title}>Charge par groupe</h4>
        <div style={styles.error}>{error}</div>
      </div>
    );
  }

  const groups = data?.groups || [];

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h4 style={styles.title}>Charge par groupe</h4>
        <span style={styles.subtitle}>(semaine)</span>
      </div>

      {groups.length === 0 ? (
        <div style={styles.empty}>
          <span style={styles.emptyIcon}>📊</span>
          <p style={styles.emptyText}>Aucun groupe configuré</p>
        </div>
      ) : (
        <div style={styles.list}>
          {groups.map((group) => (
            <div key={group.group_id} style={styles.groupItem}>
              <div style={styles.groupHeader}>
                <span style={styles.groupName}>{group.group_name}</span>
                <span style={styles.groupStats}>
                  {group.on_mission}/{group.total_members} pers.
                </span>
              </div>
              <div style={styles.progressBar}>
                <div
                  style={{
                    ...styles.progressFill,
                    width: `${Math.min(group.workload_percent, 100)}%`,
                    backgroundColor: getBarColor(group.workload_percent)
                  }}
                />
              </div>
              <span style={{
                ...styles.percentLabel,
                color: getBarColor(group.workload_percent)
              }}>
                {group.workload_percent}%
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '20px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
  },
  header: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '8px',
    marginBottom: '16px'
  },
  title: {
    margin: 0,
    fontSize: '1.1rem',
    fontWeight: '600',
    color: '#1a1a2e'
  },
  subtitle: {
    fontSize: '0.85rem',
    color: '#6c757d'
  },
  loading: {
    textAlign: 'center',
    padding: '30px',
    color: '#6c757d'
  },
  error: {
    padding: '20px',
    backgroundColor: '#fff5f5',
    borderRadius: '8px',
    color: '#dc3545',
    textAlign: 'center'
  },
  empty: {
    textAlign: 'center',
    padding: '30px'
  },
  emptyIcon: {
    fontSize: '2rem',
    display: 'block',
    marginBottom: '8px'
  },
  emptyText: {
    margin: 0,
    color: '#6c757d'
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  groupItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  groupHeader: {
    minWidth: '140px'
  },
  groupName: {
    display: 'block',
    fontSize: '0.9rem',
    fontWeight: '500',
    color: '#1a1a2e'
  },
  groupStats: {
    fontSize: '0.8rem',
    color: '#6c757d'
  },
  progressBar: {
    flex: 1,
    height: '12px',
    backgroundColor: '#e9ecef',
    borderRadius: '6px',
    overflow: 'hidden'
  },
  progressFill: {
    height: '100%',
    borderRadius: '6px',
    transition: 'width 0.3s ease'
  },
  percentLabel: {
    minWidth: '40px',
    textAlign: 'right',
    fontSize: '0.9rem',
    fontWeight: '600'
  }
};

export default GroupWorkload;
